'use client';

import { FC, useCallback, useState } from 'react';
import { Upload, X, Camera, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LogoUploadProps {
  onFileSelect: (file: File) => void;
  currentFile?: File | null;
  currentUrl?: string | null;
  onRemove: () => void;
  disabled?: boolean;
}

export const LogoUpload: FC<LogoUploadProps> = ({
  onFileSelect,
  currentFile,
  currentUrl,
  onRemove,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(
    currentFile ? URL.createObjectURL(currentFile) : currentUrl || null
  );

  const handleFile = useCallback(
    (file: File) => {
      // Validate file type (only images for logos)
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file (JPG, PNG, GIF, etc.)');
        return;
      }

      // Validate file size (max 5MB for logos)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      onFileSelect(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [onFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleCameraClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera on mobile
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFile(file);
      }
    };
    input.click();
  }, [handleFile]);

  const handleRemove = useCallback(() => {
    setPreview(null);
    onRemove();
  }, [onRemove]);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Company Logo</label>
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 dark:border-gray-700',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {preview ? (
          <div className="relative">
            <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <img
                src={preview}
                alt="Logo preview"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={handleRemove}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800">
              <ImageIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">
                Drag and drop your logo here
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                or click to browse
              </p>
            </div>
            <div className="flex gap-2">
              <label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  disabled={disabled}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  asChild
                >
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </span>
                </Button>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCameraClick}
                disabled={disabled}
              >
                <Camera className="h-4 w-4 mr-2" />
                Camera
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Supports JPG, PNG, GIF, WebP (max 5MB)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
